import { useState } from 'react'
import { useMatch } from 'react-router-dom'
import { faThumbsUp as faThumbsUpSolid } from '@fortawesome/pro-solid-svg-icons/faThumbsUp'
import { faMessage } from '@fortawesome/pro-light-svg-icons/faMessage'
import { faThumbsUp } from '@fortawesome/pro-light-svg-icons/faThumbsUp'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { Icon } from 'src/components'
import i18n from 'src/i18n'
import { Surveys, SurvicateUtils } from 'src/utils/survicate'
import { deleteLike, putLike } from 'src/client'
import {
  Actions,
  ComponentNames,
  ComponentSources,
  Entities,
  EventNames,
  ScreenNames
} from 'src/utils/datahub/enums'
import { paths } from 'src/router/paths'
import { toastEventDispatch } from '@hotmart/space-lib-events/dist/toast'
import { ToastPosition, ToastType } from '@hotmart/space-lib-events/dist/toast/enum'
import usePublicCommunity from 'src/hook/usePublicCommunity'
import Button from '@hotmart/space-lib/dist/components/button'
import clsx from 'clsx'
import usePublicCommunityReactionClick from 'src/hook/usePublicCommunityReactionClick/usePublicCommunityReactionClick'
import { CommentReactionProps, ReactionType } from './commentReaction.types'

const CommentReaction = ({
  commentId,
  openInputAnswer,
  setOpenInputAnswer,
  reactionsCount,
  parentCommentId,
  userReaction,
  onSuccess,
  canUserRespondToComment
}: CommentReactionProps) => {
  const { sendDataHubConsume } = useSendDataHubConsume()
  const [isLikeDisable, setIsLikeDisable] = useState(false)
  const { setScreenName, setComponentSource } = usePublicCommunity()
  const [commentLiked, setCommentLiked] = useState(userReaction === ReactionType.LIKE)
  const { shouldCallReactionClick, handlePublicCommunityReactionClick } =
    usePublicCommunityReactionClick()

  const userLike = userReaction === ReactionType.LIKE ? 1 : 0
  const likeCountInitialState = reactionsCount?.LIKE || userLike
  const [likeCount, setLikeCount] = useState(likeCountInitialState)

  const showAnswerButton = !parentCommentId
  const shouldShowAnswerButton = showAnswerButton && canUserRespondToComment

  const likeOrLikes =
    likeCount && likeCount > 1 ? i18n.t('comments.likes') : i18n.t('comments.like')
  const screenName = useMatch(paths.COMMUNITY_POST_PATH)
    ? ScreenNames.COMMUNITY_POST_DETAIL
    : ScreenNames.COMMUNITY_FEED

  const handleDeslike = async (commentId: number) => {
    try {
      setIsLikeDisable(true)
      setCommentLiked(false)
      await deleteLike(commentId)
      onSuccess?.()
      setLikeCount((oldValue: number) => oldValue - 1)
    } catch {
      setCommentLiked(true)
      toastEventDispatch({
        type: ToastType.DANGER,
        message: i18n.t('comments.like_error'),
        position: ToastPosition.BOTTOM_RIGHT
      })
    } finally {
      setIsLikeDisable(false)
    }
  }

  const handleLike = async (commentId: number) => {
    try {
      setIsLikeDisable(true)
      SurvicateUtils.showSurvey(Surveys.LIKE_ACTION)
      setCommentLiked(true)
      await putLike(commentId)
      onSuccess?.()
      setLikeCount((oldValue: number) => oldValue + 1)
    } catch {
      setCommentLiked(false)
      toastEventDispatch({
        type: ToastType.DANGER,
        message: i18n.t('comments.like_error'),
        position: ToastPosition.BOTTOM_RIGHT
      })
    } finally {
      setIsLikeDisable(false)
    }
  }

  const handleToogleLike = async (commentId: number) => {
    if (shouldCallReactionClick) {
      setScreenName(screenName)
      setComponentSource(ComponentSources.LIKE_BUTTON)

      handlePublicCommunityReactionClick()
      return
    }

    if (commentLiked) {
      handleDeslike(commentId)
      return
    }

    handleLike(commentId)
  }

  const handleAnswer = () => {
    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.CLICK_TO_REPLY_TO_COMMENT,
        screenName: ScreenNames.COMMUNITY_POST_COMMENT,
        componentName: ComponentNames.COMMENT_ITEM
      }
    })

    if (shouldCallReactionClick) {
      setScreenName(screenName)
      setComponentSource(ComponentSources.POST_FIELD)

      handlePublicCommunityReactionClick()
      return
    }

    setOpenInputAnswer(!openInputAnswer)
  }

  return (
    <div
      className="_d-flex _align-items-center _justify-content-between"
      data-test="comment-reaction"
    >
      <div className="_d-flex _align-items-center _mt-2">
        <Button
          variation="tertiary"
          className="_mr-1"
          disabled={isLikeDisable}
          onClick={() => handleToogleLike(commentId)}
        >
          <Icon icon={commentLiked ? faThumbsUpSolid : faThumbsUp} />
          <span
            data-test="span-like"
            className={clsx('_ml-2', {
              '_font-weight-bold': commentLiked,
              '_font-weight': !commentLiked
            })}
          >
            {i18n.t('reaction.like')}
          </span>
        </Button>

        {shouldShowAnswerButton && (
          <Button variation="tertiary" onClick={handleAnswer}>
            <Icon icon={faMessage} />
            <span className="_ml-2">{i18n.t('comments.button_answer')}</span>
          </Button>
        )}
      </div>

      {Boolean(likeCount) && (
        <>
          <div data-test="like-count" className="_text-gray-600">
            {likeCount} {likeOrLikes}
          </div>
        </>
      )}
    </div>
  )
}

export default CommentReaction
